'use client';

/**
 * PortalLockScreen
 *
 * Full-screen lock overlay for the web portal and desktop that appears after
 * 1 hour of inactivity. The session stays alive (tokens keep refreshing),
 * but the UI is obscured until the user re-authenticates.
 *
 * Unlock methods (shown conditionally based on what the user has configured):
 * 1. 6-digit PIN — if the user has a PIN set
 * 2. Password — always available as an alternative
 * 3. Passkey — if the user has WebAuthn credentials
 * 4. SSO providers — only for providers the user has linked (Google, Microsoft, etc.)
 * 5. Sign out — redirects to login page
 *
 * @author TotalAccess Development Team
 * @date February 2026
 */

import type { CSSProperties } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Lock, LogOut, AlertTriangle, Timer, Key } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import '@/app/professional-modern.css';
import ThemeToggle from '@/components/ThemeToggle';
import useAuthPageBranding from '@/hooks/useAuthPageBranding';
import { authService } from '@/lib/authService';
import { getPortalAppHeaders } from '@/lib/pwaAppIdentity';
import { readRememberedUsers } from '@/lib/rememberedUsers';

interface LockScreenVerifyResponse {
  success?: boolean;
  verified?: boolean;
  locked?: boolean;
  force_logout?: boolean;
  locked_until?: string | null;
  remaining_seconds?: number;
  attempts_remaining?: number;
  error?: string;
}

interface SsoProfile {
  google_id: string | null;
  google_email: string | null;
  microsoft_id: string | null;
  microsoft_email: string | null;
  intuit_id: string | null;
  intuit_email: string | null;
  xero_id: string | null;
  xero_email: string | null;
}

interface PasskeyVerifyResponse {
  success?: boolean;
  session_token?: string;
  access_token?: string;
  user?: { id: number; email: string; first_name?: string; last_name?: string };
  error?: string;
  requires_mfa?: boolean;
  mfa_session_token?: string;
  enabled_methods?: string[];
}

interface PortalLockScreenProps {
  onUnlock: () => Promise<void>;
  onLogout: () => void;
}

const AUTH_HERO_IMAGE_VAR = '--auth-hero-image' as const;

type UnlockMode = 'pin' | 'password';

export default function PortalLockScreen({ onUnlock, onLogout }: PortalLockScreenProps) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [checkingPin, setCheckingPin] = useState(true);
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userAvatar, setUserAvatar] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [unlockMode, setUnlockMode] = useState<UnlockMode>('pin');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [ssoProfile, setSsoProfile] = useState<SsoProfile | null>(null);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const verifyInFlightRef = useRef(false);
  const {
    isDesktopShell,
    isNativePlatform,
    authBackgroundUrl,
    authLayout,
  } = useAuthPageBranding();
  const backdropStyle = authBackgroundUrl
    ? ({ [AUTH_HERO_IMAGE_VAR]: `url(${authBackgroundUrl})` } as CSSProperties)
    : undefined;

  const readAuthTokens = useCallback(() => {
    const currentTokens = authService.getTokens();
    if (currentTokens?.access_token) {
      return currentTokens;
    }

    try {
      const tokensStr = localStorage.getItem('auth_tokens');
      return tokensStr ? JSON.parse(tokensStr) : null;
    } catch {
      return null;
    }
  }, []);

  const readAuthUser = useCallback(() => {
    const currentUser = authService.getUser();
    if (currentUser) {
      return currentUser;
    }

    try {
      const userStr = localStorage.getItem('user_data') || localStorage.getItem('auth_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, []);

  // Safety check: if lock screen is shown but there are no valid tokens,
  // immediately redirect to login instead of trapping the user
  useEffect(() => {
    // Move focus off whatever element was focused when the lock fired — the
    // overlay blocks pointer input, and `inert` (SessionProvider) blocks
    // keyboard/focus access to the app, but an already-focused element could
    // otherwise retain focus briefly after mount.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    try {
      const tokens = readAuthTokens();
      if (!tokens) {
        console.log('[LockScreen] No auth tokens found on mount — redirecting to login');
        onLogout();
        return;
      }
      if (!tokens.access_token) {
        console.log('[LockScreen] Invalid auth tokens on mount — redirecting to login');
        onLogout();
      }
    } catch {
      onLogout();
    }
  }, [onLogout, readAuthTokens]);

  // Lockout countdown timer
  useEffect(() => {
    if (!lockoutUntil) {
      setLockoutRemaining(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockoutUntil.getTime() - Date.now()) / 1000));
      setLockoutRemaining(remaining);
      if (remaining <= 0) {
        setLockoutUntil(null);
        setAttemptsRemaining(null);
        setError(null);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  // Load user info from stored auth data. Seeds hasPin from the locally stored
  // user record (recorded at login) so PIN mode renders immediately instead of
  // flashing the password fallback while pin/status is in flight.
  useEffect(() => {
    const user = readAuthUser();
    if (user) {
      setUserName(user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.email);
      setUserEmail(user.email || '');
      setUserAvatar(user.avatar_url || '');
      setUserId(user.id ?? null);
      if (typeof user.has_pin === 'boolean') {
        setHasPin(user.has_pin);
      }
    }

    // Fill gaps from the remembered-users list (recorded at login time)
    try {
      const remembered = readRememberedUsers();
      const match = Array.isArray(remembered)
        ? remembered.find((u) => u?.email && user?.email && u.email.toLowerCase() === String(user.email).toLowerCase())
        : null;
      if (match) {
        if (!user?.avatar_url && match.avatarUrl) {
          setUserAvatar(match.avatarUrl);
        }
        if (typeof user?.has_pin !== 'boolean' && typeof match.hasPin === 'boolean') {
          setHasPin(match.hasPin);
        }
      }
    } catch {
      // Non-critical — avatar/PIN hint just stays unset
    }
  }, [readAuthUser]);

  const checkPinStatus = useCallback(async () => {
    setCheckingPin(true);
    try {
      const tokens = readAuthTokens();
      if (!tokens?.access_token) {
        setHasPin(false);
        setCheckingPin(false);
        return;
      }

      const response = await fetch('/api/v1/auth/pin/status', {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` }
      });

      if (response.status === 401 || response.status === 403) {
        console.log('[LockScreen] Session expired while checking PIN status — redirecting to login');
        onLogout();
        return;
      }

      // Only overwrite the locally-known PIN status when the server gives a
      // definitive answer — a network/5xx failure must not silently hide the
      // PIN UI (pin/verify still enforces the PIN server-side).
      const data = await response.json().catch(() => null);
      if (response.ok && data && typeof data.hasPin === 'boolean') {
        setHasPin(Boolean(data.success && data.hasPin));
      }
    } catch {
      // Keep the local hasPin hint on transient failures
    } finally {
      setCheckingPin(false);
    }
  }, [onLogout, readAuthTokens]);

  // Check if user has a PIN set
  useEffect(() => {
    checkPinStatus();
  }, [checkPinStatus]);

  // Fetch SSO profile and passkey availability for the locked user
  useEffect(() => {
    const fetchSsoProfile = async () => {
      const user = readAuthUser();
      if (!user?.id) return;
      try {
        const res = await fetch(`/api/v1/auth/sso-profile?user_id=${user.id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.profile && data.profile.userId > 0) {
            setSsoProfile({
              google_id: data.profile.google_id ?? null,
              google_email: data.profile.google_email ?? null,
              microsoft_id: data.profile.microsoft_id ?? null,
              microsoft_email: data.profile.microsoft_email ?? null,
              intuit_id: data.profile.intuit_id ?? null,
              intuit_email: data.profile.intuit_email ?? null,
              xero_id: data.profile.xero_id ?? null,
              xero_email: data.profile.xero_email ?? null,
            });
          }
        }
      } catch {
        // Non-critical — SSO buttons just won't show
      }
    };

    const checkPasskeyAvailability = async () => {
      const user = readAuthUser();
      if (!user?.id) return;
      try {
        const res = await fetch('/api/v1/auth/webauthn/authenticate/options', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
          body: JSON.stringify({ userId: user.id }),
        });
        if (res.ok) {
          const data = await res.json();
          // If the server returns options with allowCredentials, the user has passkeys
          if (data.success && data.options?.allowCredentials?.length > 0) {
            setHasPasskey(true);
          }
        }
      } catch {
        // Non-critical — passkey button just won't show
      }
    };

    fetchSsoProfile();
    checkPasskeyAvailability();
  }, [readAuthUser]);

  // Helper: process lockout / attempts-remaining from API responses
  // Returns true if unlock succeeded, false otherwise.
  // If the session is expired (401/403), triggers logout automatically.
  const handleVerifyResponse = (data: LockScreenVerifyResponse, status: number): boolean => {
    if (data.force_logout) {
      console.log('[LockScreen] Unlock session revoked after repeated failures — redirecting to login');
      onLogout();
      return false;
    }
    // Session expired — tokens are invalid, redirect to login
    if (status === 401 || status === 403) {
      console.log('[LockScreen] Session expired (HTTP %d) — redirecting to login', status);
      onLogout();
      return false;
    }
    if (status === 423 && data.locked) {
      // Account locked
      const until = data.locked_until ? new Date(data.locked_until) : new Date(Date.now() + (data.remaining_seconds || 900) * 1000);
      setLockoutUntil(until);
      setAttemptsRemaining(0);
      setError(data.error || 'Too many failed attempts.');
      return false;
    }
    if (data.attempts_remaining !== undefined) {
      setAttemptsRemaining(data.attempts_remaining);
    }
    return Boolean(data.success);
  };

  const isLockedOut = lockoutUntil !== null && lockoutRemaining > 0;

  // Format lockout remaining as M:SS
  const formatLockoutTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleUnlockSuccess = useCallback(async () => {
    setPinDigits(['', '', '', '', '', '']);
    setPassword('');
    setAttemptsRemaining(null);
    await onUnlock();
  }, [onUnlock]);

  // Attempt PIN unlock
  const handlePinUnlock = useCallback(async (pin: string) => {
    if (pin.length !== 6 || isLockedOut || verifyInFlightRef.current) return;

    verifyInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const tokens = readAuthTokens();
      if (!tokens) throw new Error('No auth data');

      const response = await fetch('/api/v1/auth/pin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ pin })
      });

      const data = await response.json();
      const success = handleVerifyResponse(data, response.status);

      if (success) {
        console.log('[LockScreen] PIN verification successful - unlocking');
        await handleUnlockSuccess();
      } else if (!data.locked) {
        setError(data.error || 'Incorrect PIN. Please try again.');
        setPinDigits(['', '', '', '', '', '']);
        pinInputRefs.current[0]?.focus();
      } else {
        // Locked out — clear digits
        setPinDigits(['', '', '', '', '', '']);
      }
    } catch (err: unknown) {
      console.error('[LockScreen] PIN verification error:', err);
      // Check if tokens are still valid
      const tokensCheck = readAuthTokens();
      if (!tokensCheck) {
        onLogout();
        return;
      }
      setError('Verification failed. Please try again.');
      setPinDigits(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
      verifyInFlightRef.current = false;
    }
  }, [handleUnlockSuccess, isLockedOut, onLogout, readAuthTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attempt password unlock
  const handlePasswordUnlock = useCallback(async () => {
    // Password managers can fill the DOM input without an input event React
    // observes — fall back to the live DOM value so autofilled credentials
    // still submit instead of silently no-op'ing on an empty state value.
    const effectivePassword = password || passwordInputRef.current?.value || '';
    if (!effectivePassword || isLockedOut || verifyInFlightRef.current) return;

    verifyInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const tokens = readAuthTokens();
      if (!tokens) throw new Error('No auth data');

      const response = await fetch('/api/v1/auth/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ password: effectivePassword })
      });

      const data = await response.json();
      const success = handleVerifyResponse(data, response.status);

      if (success) {
        console.log('[LockScreen] Password verification successful - unlocking');
        await handleUnlockSuccess();
      } else if (!data.locked) {
        setError(data.error || 'Incorrect password. Please try again.');
        setPassword('');
        passwordInputRef.current?.focus();
      } else {
        setPassword('');
      }
    } catch (err: unknown) {
      console.error('[LockScreen] Password verification error:', err);
      const tokensCheck = readAuthTokens();
      if (!tokensCheck) {
        onLogout();
        return;
      }
      setError('Verification failed. Please try again.');
      setPassword('');
    } finally {
      setLoading(false);
      verifyInFlightRef.current = false;
    }
  }, [handleUnlockSuccess, isLockedOut, onLogout, password, readAuthTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attempt passkey unlock
  const handlePasskeyUnlock = useCallback(async () => {
    if (!userId || isLockedOut || verifyInFlightRef.current) return;

    verifyInFlightRef.current = true;
    setPasskeyLoading(true);
    setError(null);

    try {
      // Get authentication options from server
      const optionsResponse = await fetch('/api/v1/auth/webauthn/authenticate/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
        body: JSON.stringify({ userId }),
      });

      const optionsData = await optionsResponse.json();
      if (!optionsResponse.ok) {
        throw new Error(optionsData.error || 'Failed to get passkey options');
      }

      // Start WebAuthn authentication ceremony
      const authResponse = await startAuthentication(optionsData.options);

      // Verify authentication with server
      const verifyResponse = await fetch('/api/v1/auth/webauthn/authenticate/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
        body: JSON.stringify({
          userId: optionsData.userId,
          response: authResponse,
          challenge: optionsData.options.challenge,
        }),
      });

      const verifyData: PasskeyVerifyResponse = await verifyResponse.json();
      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Passkey verification failed');
      }

      if (verifyData.success) {
        console.log('[LockScreen] Passkey verification successful - unlocking');
        // If the verify endpoint returned new tokens, update the auth service
        if (verifyData.session_token && verifyData.user) {
          await authService.setOAuthAuth(
            verifyData.session_token,
            verifyData.user,
            verifyData.access_token || undefined,
          );
        }
        await handleUnlockSuccess();
      }
    } catch (err: unknown) {
      console.error('[LockScreen] Passkey verification error:', err);
      // Don't show "abort" errors as actual errors — user just cancelled
      const msg = err instanceof Error ? err.message : String(err);
      if (!/abort/i.test(msg)) {
        setError(msg || 'Passkey verification failed. Please try again.');
      }
    } finally {
      setPasskeyLoading(false);
      verifyInFlightRef.current = false;
    }
  }, [handleUnlockSuccess, isLockedOut, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle SSO sign in — redirect through OAuth flow, which will return to the portal
  const handleSsoSignIn = useCallback((provider: 'google' | 'microsoft' | 'intuit' | 'xero') => {
    const hint = ssoProfile ? ssoProfile[`${provider}_email` as keyof SsoProfile] : null;
    const hasProviderId = ssoProfile ? Boolean(ssoProfile[`${provider}_id` as keyof SsoProfile]) : false;
    // For Google, skip account selection if the user has a linked ID
    const skipSelection = provider === 'google' && hasProviderId;
    // Pass the current page as the redirect target so the user returns to
    // where they were after OAuth completes. The oauth-success page clears
    // the lock state after successful authentication.
    const currentPath = typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : undefined;
    authService.initiateOAuthFlow(provider, currentPath, hint ?? undefined, skipSelection || undefined);
  }, [ssoProfile]);

  // Handle PIN digit input
  const handlePinDigitChange = useCallback((index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return; // Only allow single digits

    const newDigits = [...pinDigits];
    newDigits[index] = value;
    setPinDigits(newDigits);
    setError(null);

    if (value && index < 5) {
      // Auto-focus next input
      pinInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when the last empty digit is filled — pinDigits[index] === ''
    // ensures this only fires on the completing entry, so editing an existing
    // digit (or filling boxes out of order) doesn't double-submit.
    if (value && pinDigits[index] === '' && newDigits.every(d => d !== '')) {
      handlePinUnlock(newDigits.join(''));
    }
  }, [pinDigits, handlePinUnlock]);

  // Handle PIN keydown (backspace navigation)
  const handlePinKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      const newDigits = [...pinDigits];
      newDigits[index - 1] = '';
      setPinDigits(newDigits);
      pinInputRefs.current[index - 1]?.focus();
    }
  }, [pinDigits]);

  // Handle PIN paste
  const handlePinPaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newDigits = pasted.split('');
      setPinDigits(newDigits);
      handlePinUnlock(pasted);
    }
  }, [handlePinUnlock]);

  // Auto-focus PIN input when PIN mode is ready
  useEffect(() => {
    if (!checkingPin && hasPin && unlockMode === 'pin') {
      setTimeout(() => pinInputRefs.current[0]?.focus(), 100);
    }
  }, [checkingPin, hasPin, unlockMode]);

  // Auto-focus password input when password mode is selected
  useEffect(() => {
    if (unlockMode === 'password' && !loading) {
      setTimeout(() => passwordInputRef.current?.focus(), 100);
    }
  }, [unlockMode, loading]);

  // Sync password-manager autofill that fills the input without firing events
  // React observes — polls briefly while the password field is mounted; clears
  // itself once the input has a value and never overwrites user-typed state.
  useEffect(() => {
    if (checkingPin || (unlockMode === 'pin' && hasPin)) {
      return;
    }

    const interval = window.setInterval(() => {
      const input = passwordInputRef.current;
      if (!input?.value) {
        return;
      }
      window.clearInterval(interval);
      setPassword((current) => current || input.value);
    }, 300);

    const timeout = window.setTimeout(() => window.clearInterval(interval), 5000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [checkingPin, unlockMode, hasPin]);

  // Default to password mode if user has no PIN
  useEffect(() => {
    if (!checkingPin && !hasPin) {
      setUnlockMode('password');
    }
  }, [checkingPin, hasPin]);

  const hasHeroBackdrop = Boolean(authBackgroundUrl);
  const shouldUseLocalPageBackground = isNativePlatform || !isDesktopShell;

  const getBackgroundStyle = () => {
    if (authBackgroundUrl) {
      return {
        backgroundImage: `url(${authBackgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      };
    }

    return {
      background: '#000000',
    };
  };

  // Determine which SSO providers to show
  // Determine which SSO providers to show (matches login page filtering)
  const showGoogleSso = Boolean(ssoProfile?.google_id);
  const showMicrosoftSso = Boolean(ssoProfile?.microsoft_id);
  const showIntuitSso = Boolean(ssoProfile?.intuit_id);
  const showXeroSso = Boolean(ssoProfile?.xero_id);
  const hasAnySsoOption = showGoogleSso || showMicrosoftSso || showIntuitSso || showXeroSso;

  // Whether to show the mode toggle between PIN and password
  const canToggleMode = hasPin;

  return (
    <div
      className={`auth-shell fixed inset-0 z-[9999] min-h-screen flex flex-col transition-all duration-300 ${isDesktopShell ? 'auth-shell--desktop' : ''} ${authBackgroundUrl ? 'auth-shell--has-background' : ''}`}
      data-auth-desktop-shell={isDesktopShell ? 'true' : 'false'}
      data-auth-layout={authLayout}
      data-auth-page="default"
    >
      {isDesktopShell && (
        <>
          <div
            className={`auth-shell__backdrop ${authBackgroundUrl ? 'auth-shell__backdrop--image' : ''}`}
            style={backdropStyle}
            aria-hidden="true"
          />
          <div className="auth-shell__backdrop-overlay" aria-hidden="true" />
          <div className="auth-shell__panel-controls">
            <div className="auth-shell__panel-controls-inner">
              <div className="auth-shell__theme-toggle" aria-label="Toggle theme">
                <ThemeToggle />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="auth-shell__page-slot relative z-[1] flex min-h-screen flex-col">
        <div className="auth-wrapper min-h-screen">
          <div
            className={`min-h-screen flex items-center justify-center px-3 py-4 sm:p-4 relative ${shouldUseLocalPageBackground && !hasHeroBackdrop ? 'pm-gradient-hero' : ''}`}
            style={shouldUseLocalPageBackground ? getBackgroundStyle() : undefined}
          >
      <div className={`pm-card max-w-md w-full p-4 sm:p-6 md:p-8 relative pm-animate-fade-in-up ${shouldUseLocalPageBackground && hasHeroBackdrop ? 'z-10' : ''}`}>
        {!isDesktopShell && (
        <div className="absolute top-3 right-3 z-10">
          <ThemeToggle />
        </div>
        )}

        <div className="text-center mb-6 sm:mb-8">
          <div className="bg-gradient-to-br from-teal-500 to-blue-600 w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
            <Lock className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
          </div>
          <h1 className="pm-auth-heading mb-2">Portal Locked</h1>
          <p className="pm-text-secondary text-sm flex items-center justify-center gap-1.5">
            <Lock className="w-3.5 h-3.5" />
            Locked due to inactivity
          </p>
        </div>

        {/* Locked user card — matches the login page's remembered-user card.
            No Switch User: the session stays active, so switching accounts
            requires Sign Out below. */}
        {(userName || userEmail) && (
          <div className="mb-4">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4 flex items-center gap-3">
              {userAvatar ? (
                <img
                  src={userAvatar}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
              )}
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">SIGNED IN AS</div>
                {userName && (
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{userName}</div>
                )}
                {userEmail && (
                  <div className={`${userName ? 'text-xs text-gray-500 dark:text-gray-400' : 'text-sm font-medium text-gray-900 dark:text-white'} truncate`}>
                    {userEmail}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lockout warning */}
        {isLockedOut && (
          <div className="pm-alert pm-alert-warning mb-4 flex items-start gap-2">
            <Timer className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Account temporarily locked</p>
              <p className="text-xs mt-1">Too many failed attempts. Try again in <span className="font-mono font-bold">{formatLockoutTime(lockoutRemaining)}</span></p>
            </div>
          </div>
        )}

        {/* Attempts remaining warning */}
        {!isLockedOut && attemptsRemaining !== null && attemptsRemaining > 0 && attemptsRemaining <= 3 && (
          <div className="pm-alert pm-alert-warning mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining before sign out
            </span>
          </div>
        )}

        {/* Error message */}
        {error && !isLockedOut && (
          <div className="pm-alert pm-alert-error mb-4">
            {error}
          </div>
        )}

        {/* Mode toggle (PIN <-> Password) — matches login page styling */}
        {canToggleMode && !isLockedOut && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              type="button"
              onClick={() => { setUnlockMode('password'); setError(null); setPassword(''); }}
              className={`${unlockMode === 'password' ? 'pm-btn-primary' : 'pm-btn-outline'} box-border h-7 px-2 !py-0 text-xs leading-none`}
            >
              Password
            </button>
            <button
              type="button"
              onClick={() => { setUnlockMode('pin'); setError(null); setPinDigits(['', '', '', '', '', '']); }}
              className={`${unlockMode === 'pin' ? 'pm-btn-primary' : 'pm-btn-outline'} box-border h-7 px-2 !py-0 text-xs leading-none`}
            >
              Use PIN
            </button>
          </div>
        )}

        {checkingPin ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
            Checking secure unlock settings…
          </div>
        ) : unlockMode === 'pin' && hasPin ? (
          <div>
            <label className="pm-label text-center block mb-3">
              Enter your 6-digit PIN
            </label>
            <div className="flex justify-center space-x-2">
              {pinDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={el => { pinInputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                  onPaste={handlePinPaste}
                  // type=text + one-time-code keeps password managers from
                  // autofilling the saved password into PIN boxes; the
                  // data-* hints cover common extension managers.
                  autoComplete="one-time-code"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  pattern="[0-9]*"
                  disabled={loading || isLockedOut || checkingPin}
                  style={{ WebkitTextSecurity: 'disc' } as CSSProperties}
                  className={`w-12 h-12 text-center text-xl font-semibold border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white transition-colors disabled:opacity-50 ${error && pinDigits.join('').length === 6
                    ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                    }`}
                />
              ))}
            </div>
            <p className="text-center text-xs pm-text-secondary mt-2">
              Your PIN unlocks automatically when the sixth digit is entered.
            </p>
          </div>
        ) : (
          /* Password unlock — matches login page input styling */
          <div>
            <label htmlFor="lock-password" className="pm-label">
              Password
            </label>
            <div className="relative">
              <input
                ref={passwordInputRef}
                id="lock-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { void handlePasswordUnlock(); } }}
                disabled={loading || isLockedOut}
                className="pm-input pr-10"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={() => void handlePasswordUnlock()}
              disabled={loading || isLockedOut || !password}
              className="pm-btn-primary w-full box-border h-7 px-2 !py-0 text-xs leading-none disabled:opacity-50 mt-3"
            >
              {loading ? 'Unlocking…' : 'Unlock'}
            </button>
          </div>
        )}

        {/* Passkey unlock — matches login page passkey button styling */}
        {hasPasskey && !isLockedOut && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => void handlePasskeyUnlock()}
              disabled={passkeyLoading || isLockedOut}
              className="pm-btn-outline w-full box-border h-7 px-2 !py-0 flex items-center justify-center gap-2 disabled:opacity-50 text-xs leading-none"
              title="Unlock with a passkey for this account"
            >
              <Key className="w-4 h-4" />
              {passkeyLoading ? '...' : 'Passkey'}
            </button>
          </div>
        )}

        {/* SSO providers — only show providers the user has linked, matching login page */}
        {hasAnySsoOption && !isLockedOut && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 pm-text-secondary">Or continue with</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {/* Google — shown if user has google_id linked */}
              {showGoogleSso && (
                <button
                  type="button"
                  onClick={() => handleSsoSignIn('google')}
                  disabled={loading || isLockedOut}
                  className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span className="ml-2">Google</span>
                </button>
              )}

              {/* Microsoft — shown if user has microsoft_id linked */}
              {showMicrosoftSso && (
                <button
                  type="button"
                  onClick={() => handleSsoSignIn('microsoft')}
                  disabled={loading || isLockedOut}
                  className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#F25022" d="M1 1h10v10H1z" />
                    <path fill="#00A4EF" d="M13 1h10v10H13z" />
                    <path fill="#7FBA00" d="M1 13h10v10H1z" />
                    <path fill="#FFB900" d="M13 13h10v10H13z" />
                  </svg>
                  <span className="ml-2">Microsoft</span>
                </button>
              )}

              {/* Intuit / QuickBooks — shown if user has intuit_id linked */}
              {showIntuitSso && (
                <button
                  type="button"
                  onClick={() => handleSsoSignIn('intuit')}
                  disabled={loading || isLockedOut}
                  className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                >
                  <img src="/bank-logos/Intuit Quickbooks.svg" alt="QuickBooks" className="h-5 w-5 object-contain" />
                  <span className="ml-2 truncate">QuickBooks</span>
                </button>
              )}

              {/* Xero — shown if user has xero_id linked */}
              {showXeroSso && (
                <button
                  type="button"
                  onClick={() => handleSsoSignIn('xero')}
                  disabled={loading || isLockedOut}
                  className="pm-btn-outline box-border h-7 px-2 !py-0 flex items-center justify-center disabled:opacity-50 text-xs leading-none"
                >
                  <img src="/bank-logos/xero.svg" alt="Xero" className="h-5 w-5 object-contain" />
                  <span className="ml-2">Xero</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Sign out — matches login page alternative action styling */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 pm-text-secondary">or</span>
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={onLogout}
              className="pm-btn-outline w-full box-border h-7 px-2 !py-0 flex items-center justify-center gap-2 text-xs leading-none"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
}
